import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectService } from '../services/api';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadProject();
    }
  }, [id]);

  const loadProject = async () => {
    try {
      const data = await projectService.getById(id!);
      setProject(data);
    } catch (err) {
      console.error('Error loading project:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-white/70">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">Project not found</h2>
          <p className="text-white/70 mb-4">The project you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/projects')}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        <button 
          onClick={() => navigate('/projects')} 
          className="mb-4 text-white/70 hover:text-white flex items-center space-x-2"
        >
          <span>←</span>
          <span>Back to Projects</span>
        </button>
        
        <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
          <h1 className="text-2xl font-bold text-white mb-4">{project.title}</h1>
          <p className="text-white/80 mb-4">{project.description}</p>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <strong className="text-white">Category:</strong> 
              <span className="text-white/80 ml-2">{project.category}</span>
            </div>
            <div>
              <strong className="text-white">Budget:</strong> 
              <span className="text-white/80 ml-2">${project.budget?.min} - ${project.budget?.max}</span>
            </div>
            <div>
              <strong className="text-white">Timeline:</strong> 
              <span className="text-white/80 ml-2">{project.timeline}</span>
            </div>
            <div>
              <strong className="text-white">Location:</strong> 
              <span className="text-white/80 ml-2">{project.location}</span>
            </div>
          </div>
          
          <div className="mb-4">
            <strong className="text-white">Skills:</strong>
            <div className="flex flex-wrap gap-2 mt-2">
              {project.skills?.map((skill: string, index: number) => (
                <span key={index} className="px-2 py-1 bg-primary-500/20 text-primary-300 rounded text-sm border border-primary-500/30">
                  {skill}
                </span>
              ))}
            </div>
          </div>
          
          <div className="text-sm text-white/50">
            Posted by {project.client_name} on {new Date(project.posted_date).toLocaleDateString()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
